import { motion } from 'framer-motion';
import {
  Sparkles,
  AlertTriangle,
  HelpCircle,
  Target,
  ListChecks,
  NotebookPen,
} from 'lucide-react';
import { TONE, FLOW_STAGE_TONE, humanize } from '../theme/adminVisualSystem';

/**
 * AnalysisSection — typed block for AI / curator content.
 *   tone: TONE token (color/border/bg/glow)
 *   icon: lucide icon component
 */
function AnalysisSection({ tone, icon: Icon, title, helper, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="border bg-[#0a0a0a] p-3.5"
      style={{
        borderColor: tone.border,
        boxShadow: `inset 0 0 0 1px transparent`,
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          aria-hidden
          className="block w-1.5 h-1.5 rounded-full"
          style={{ background: tone.text, boxShadow: `0 0 8px ${tone.glow}` }}
        />
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: tone.text }} />}
        <h4 className="font-outfit text-sm font-semibold text-white tracking-tight">
          {title}
        </h4>
      </div>
      {helper && (
        <p className="text-[10px] font-mono text-[#666] mb-2 leading-relaxed">{helper}</p>
      )}
      {children}
    </motion.section>
  );
}

function Body({ text }) {
  if (!text) return null;
  return (
    <p className="text-[12px] font-mono text-[#d6d6d6] whitespace-pre-wrap leading-relaxed">
      {text}
    </p>
  );
}

/**
 * AnalysisBlocks — the structured AI/curator review layout.
 * Renders only sections with content.
 */
export default function AnalysisBlocks({ ai, curatorNotes, currentStage }) {
  const hasAi = !!ai;
  const todoConv = ai?.todo_conversion;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {hasAi && ai.why_it_could_work && (
        <AnalysisSection tone={TONE.green} icon={Sparkles} title="Why it could work">
          <Body text={ai.why_it_could_work} />
        </AnalysisSection>
      )}

      {hasAi && ai.risks_critical_analysis && (
        <AnalysisSection tone={TONE.red} icon={AlertTriangle} title="Risks · Critical analysis">
          <Body text={ai.risks_critical_analysis} />
        </AnalysisSection>
      )}

      {hasAi && Array.isArray(ai.missing_information) && ai.missing_information.length > 0 && (
        <AnalysisSection tone={TONE.amber} icon={HelpCircle} title="Missing information">
          <ul className="space-y-1 text-[12px] font-mono text-[#d6d6d6]">
            {ai.missing_information.map((m, i) => (
              <li key={i} className="leading-relaxed">— {m}</li>
            ))}
          </ul>
        </AnalysisSection>
      )}

      {hasAi && ai.suggested_next_step && (
        <AnalysisSection tone={TONE.cyan} icon={Target} title="Suggested next step">
          <Body text={ai.suggested_next_step} />
        </AnalysisSection>
      )}

      {hasAi && ai.suggested_stage && ai.suggested_stage !== currentStage && (
        <AnalysisSection tone={TONE.cyan} icon={Target} title="Suggested stage">
          <p className="text-[12px] font-mono text-[#d6d6d6]">
            <span className="text-[#888]">Move to · </span>
            <span
              className="font-medium"
              style={{ color: FLOW_STAGE_TONE[ai.suggested_stage]?.text || '#90E0EF' }}
            >
              {humanize(ai.suggested_stage)}
            </span>
            <span className="text-[#555] block mt-1 text-[10px] tracking-[0.15em] uppercase">
              Curator approval required
            </span>
          </p>
        </AnalysisSection>
      )}

      {hasAi && todoConv && (todoConv.task_title || todoConv.task_description || todoConv.first_action) && (
        <AnalysisSection tone={TONE.green} icon={ListChecks} title="To-do conversion">
          {todoConv.task_title && (
            <p className="text-[12px] font-mono text-white font-medium mb-1">
              {todoConv.task_title}
            </p>
          )}
          {todoConv.task_description && (
            <p className="text-[12px] font-mono text-[#d6d6d6] leading-relaxed mb-1.5 whitespace-pre-wrap">
              {todoConv.task_description}
            </p>
          )}
          {todoConv.first_action && (
            <p className="text-[11px] font-mono text-[#888]">
              <span className="text-[#90E0EF]">First action · </span>
              {todoConv.first_action}
            </p>
          )}
        </AnalysisSection>
      )}

      {curatorNotes && (
        <AnalysisSection tone={TONE.cyan} icon={NotebookPen} title="Curator review notes">
          <Body text={curatorNotes} />
        </AnalysisSection>
      )}
    </div>
  );
}
